import React from 'react';
import PropTypes from 'prop-types';
import { post } from 'axios';
import Dropzone from 'react-dropzone';
import { ProgressBar } from 'react-bootstrap-5';

import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faTrash } from '@fortawesome/pro-solid-svg-icons';

import { extractNumPixelsFromImageFile } from 'helpers/image_helpers';
import './styles/image_dropzone.scss';

const defaultUploadPath = '/uploads';

class ImageDropzone extends React.Component {
  static propTypes = {
    allowedMedia: PropTypes.arrayOf(PropTypes.string),
    allowUpload: PropTypes.oneOfType([PropTypes.bool, PropTypes.func]),
    className: PropTypes.string,
    disableClick: PropTypes.bool,
    dropBox: PropTypes.node,
    dropZoneClassName: PropTypes.string,
    formDataGenerator: PropTypes.func,
    forwardedRef: PropTypes.any,
    maxPixelSize: PropTypes.number,
    preview: PropTypes.string,
    UniqueProgressBar: PropTypes.elementType,
    uploadId: PropTypes.number,
    uploadPath: PropTypes.string,
    onDelete: PropTypes.func,
    onUploadComplete: PropTypes.func.isRequired
  }

  static defaultProps = {
    formDataGenerator(file) {
      const formData = new FormData();
      formData.append('upload[file]', file);
      return formData;
    },
    allowUpload: true,
    uploadPath: defaultUploadPath,
    allowedMedia: ['application/pdf', 'image/*'],
    maxPixelSize: 64000000
  }

  static defaultUploadPath = defaultUploadPath

  state = {
    uploading: false
  }

  uploadFile = (file) => {
    const formData = this.props.formDataGenerator(file);
    const config = { onUploadProgress: this.handleUploadProgress };
    this.setState({
      fileName: formData.get('upload[file]')?.name,
      progress: 0,
      size: formData.get('upload[file]')?.size,
      uploading: true
    });
    return post(this.props.uploadPath, formData, config).then((response) => {
      this.setState({ uploading: false });
      this.props.onUploadComplete(response.data);
    });
  }

  handleImageUpload = (file) => {
    return extractNumPixelsFromImageFile(file)
      .then((numPixels) => {
        if (this.validPhotoSize(numPixels)) return file;

        const hW = Math.sqrt(this.props.maxPixelSize);
        throw new Error(`
          Please upload a smaller version of this image.<br />
          Dimensions should be no larger than <strong>${hW}x${hW}</strong>.
        `);
      })
      .then(this.uploadFile);
  }

  handleDrop = (files) => {
    if (files.length === 0) return;
    if (!this.props.allowUpload) return;

    const file = files.shift();
    if (typeof this.props.allowUpload === 'function' && !this.props.allowUpload(file)) return;

    return (file.type.includes('image') ? this.handleImageUpload : this.uploadFile)(file)
      .then(() => this.handleDrop(files))
      .catch((err) => {
        if (typeof bootbox !== 'undefined') {
          bootbox.alert({
            title: 'Upload Failed',
            message: err.message
          });
        }
      });
  }

  validPhotoSize = (pixels) => {
    const { maxPixelSize } = this.props;

    return pixels < maxPixelSize;
  }

  handleUploadProgress = (event) => {
    this.setState({ progress: Math.floor(event.loaded * 100 / event.total) });
  }

  handleRejectedFiles = () => {
    if (typeof bootbox !== 'undefined') {
      const types = this.props.allowedMedia.map((type) => {
        if (type === 'image/*') return 'Images';
        if (type === 'application/pdf') return 'PDF files';
        return type.split('/')[1]?.toUpperCase();
      }).join(', ') || 'Images and PDF files';

      bootbox.alert({
        title: 'Invalid Media Format',
        message: `
            Please upload a valid media file.
            Accepted types: <strong>${types}</strong>.
          `
      });
    }
  }

  render() {
    const {
      className,
      dropZoneClassName,
      /* eslint-disable no-unused-vars */
      formDataGenerator,
      uploadPath,
      onDelete,
      onUploadComplete,
      dropBox,
      uploadId,
      /* eslint-enable no-unused-vars */
      ...dropzoneProps
    } = this.props;

    return (
      <div className={`image-dropzone position-relative clickable ${className || ''}`}>
        <Dropzone
          accept={this.props.allowedMedia.join(',')}
          ref={this.props.forwardedRef}
          onDropAccepted={this.handleDrop}
          onDropRejected={this.handleRejectedFiles}
          {...dropzoneProps}
        >
          {({ getRootProps, getInputProps }) => (
            <div className={dropZoneClassName} {...getRootProps()}>
              {this.state.uploading ? this.renderProgressBar() : this.renderPreview()}
              <input {...getInputProps()} />
            </div>
          )}
        </Dropzone>

        {this.props.preview && this.props.onDelete &&
          <button
            className="btn btn-sm btn-danger position-absolute top-0 end-0 m-1"
            data-upload-id={this.props.uploadId}
            onClick={this.props.onDelete}
          >
            <FontAwesomeIcon icon={faTrash}/>
          </button>
        }
      </div>
    );
  }

  renderPreview() {
    if (this.props.preview) {
      return <img src={this.props.preview} />;
    } else if (this.props.dropBox) {
      return this.props.dropBox;
    } else {
      return <h6 className="text-center text-muted">Click or drag files here to upload</h6>;
    }
  }

  renderProgressBar() {
    const { UniqueProgressBar } = this.props;
    if (UniqueProgressBar) {
      return <UniqueProgressBar fileName={this.state.fileName} now={this.state.progress} size={this.state.size}/>;
    }
    return <ProgressBar active now={this.state.progress} />;
  }
}

Object.assign(ImageDropzone.propTypes, Dropzone.propTypes);

export default ImageDropzone;
